import React, { useState, useEffect } from "react";
import axios from "axios";
import Constants from "./Constants";
import { useSelector, useDispatch } from "react-redux";
import { setAdvisors, setStudents } from "./store/actions";
import DeleteAdvisorModal from "./modals/DeleteAdvisorModal";
import AddAdvisorModal from "./modals/AddAdvisorModal";
import EditAdvisorModal from "./modals/EditAdvisorModal";
import ReportModal from "./modals/ReportModal";
import {
	Box,
	Button,
	CircularProgress,
	IconButton,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Tooltip,
	Typography,
	useTheme,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import LoginIcon from "@mui/icons-material/Login";

function Advisors() {
	const dispatch = useDispatch();
	const advisors = useSelector(state => state.advisors);
	const info = useSelector(state => state.info);
	const [advisor, setAdvisor] = useState({});
	const [idToEdit, setIdToEdit] = useState(0);
	const [open, setOpen] = useState(false);
	const [editOpen, setEditOpen] = useState(false);
	const [delOpen, setDelOpen] = useState(false);
	const [deleteMsg, setDeleteMsg] = useState(null);
	const [fetchMsg, setFetchMsg] = useState(null);
	const [addMsg, setAddMsg] = useState(null);
	const [editMsg, setEditMsg] = useState(null);
	const [idToDelete, setIdToDelete] = useState(0);
	const [fullNameToDelete, setFullNameToDelete] = useState("");
	const [loading, setLoading] = useState(false);
	const [reportModal, setReportModal] = useState(false);

	const theme = useTheme();

	const addAdvisorApi = async advisor => {
		try {
			const response = await axios.post(
				Constants.baseUrl + "/school/advisor/add",
				{
					advisor: {
						firstName: advisor.firstName,
						lastName: advisor.lastName,
						mail: advisor.mail,
						mobile: advisor.mobile,
						gender: advisor.gender,
						bio: advisor.bio,
						capacity: advisor.capacity,
						link: advisor.link,
						city: advisor.city,
						psychologicBio: advisor.psychologicBio,
					},
				},
				{
					headers: {
						authorization: "Bearer " + localStorage.getItem("schoolToken"),
					},
				}
			);
			if (response.data.status === 201) {
				fetchData();
				setAddMsg({
					type: true,
					header: "عملیات با موفقیت انجام شد.",
					message: "مشاور اضافه شد",
				});
				setOpen(false);
			} else {
				setAddMsg({
					type: false,
					header: "تلاش ناموفق",
					message: response.data.result.message,
				});
			}
		} catch (err) {
			setAddMsg({
				type: false,
				header: "تلاش ناموفق",
				message: "خطا در برقراری ارتباط",
			});
		}
	};

	const editAdvisorApi = async (advisor, id) => {
		try {
			const response = await axios.patch(
				Constants.baseUrl + "/school/advisor/" + id,
				{
					advisor: {
						firstName: advisor.firstName,
						lastName: advisor.lastName,
						mail: advisor.mail,
						mobile: advisor.mobile,
						gender: advisor.gender,
						bio: advisor.bio,
						capacity: advisor.capacity,
						link: advisor.link,
						city: advisor.city,
						psychologicBio: advisor.psychologicBio,
					},
				},
				{
					headers: {
						authorization: "Bearer " + localStorage.getItem("schoolToken"),
					},
				}
			);
			if (response.data.status === 202) {
				fetchData();
				setEditMsg({
					type: true,
					header: "عملیات با موفقیت انجام شد.",
					message: "اطلاعات مشاور ویرایش شد",
				});
				setEditOpen(false);
			} else {
				setEditMsg({
					type: false,
					header: "تلاش ناموفق",
					message: response.data.result.message,
				});
			}
		} catch (err) {
			setEditMsg({
				type: false,
				header: "تلاش ناموفق",
				message: "خطا در برقراری ارتباط",
			});
		}
	};

	const deleteApi = async id => {
		try {
			const response = await axios.delete(
				Constants.baseUrl + "/school/advisor/" + id,
				{
					headers: {
						authorization: "Bearer " + localStorage.getItem("schoolToken"),
					},
				}
			);
			const status = response.data.status;
			if (status === 202) {
				fetchData();
				setDeleteMsg({
					type: true,
					header: "عملیات با موفقیت انجام شد.",
					message: "مشاور با موفقیت حذف شد.",
				});
			} else {
				setDeleteMsg({
					type: false,
					header: "تلاش ناموفق",
					message: response.data.result.message,
				});
			}
		} catch (err) {
			setDeleteMsg({
				type: false,
				header: "تلاش ناموفق",
				message: "خطا در برقراری ارتباط",
			});
		}
	};

	const fetchData = async () => {
		try {
			setLoading(true);
			setFetchMsg(null);
			const [advisorsResponse, studentsResponse] = await Promise.all([
				axios.get(Constants.baseUrl + "/school/advisors", {
					headers: {
						authorization: "Bearer " + localStorage.getItem("schoolToken"),
					},
				}),
				axios.get(Constants.baseUrl + "/school/list/student", {
					headers: {
						authorization: "Bearer " + localStorage.getItem("schoolToken"),
					},
				}),
			]);
			if (advisorsResponse.data.status === 202) {
				dispatch(setAdvisors(advisorsResponse.data.result.advisor));
			} else {
				setFetchMsg({
					header: "تلاش ناموفق",
					message: advisorsResponse.data.result.message,
				});
			}
			if (studentsResponse.data.status === 202) {
				dispatch(setStudents(studentsResponse.data.result.members));
			} else {
				setFetchMsg({
					header: "تلاش ناموفق",
					message: studentsResponse.data.result.message,
				});
			}
		} catch (err) {
			setFetchMsg({
				header: "تلاش ناموفق",
				message: "خطا در برقراری ارتباط",
			});
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		fetchData();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<>
			<Box
				display={"flex"}
				justifyContent={"space-around"}
				alignItems={"center"}
				sx={{
					[theme.breakpoints.down("md")]: { flexDirection: "column", gap: "10px" },
				}}
				mb={2}>
				<Typography fontSize={22} fontWeight={"bolder"}>
					لیست مشاوران
				</Typography>
				<Box className="d-flex" flexWrap={"wrap"}>
					<Button
						onClick={() => {
							setOpen(true);
							setAddMsg(null);
						}}
						sx={{ height: "45px", fontWeight: "bold", m: 1 }}
						variant="contained"
						size="large">
						افزودن مشاور
					</Button>
					<Button
						onClick={() => {
							setReportModal(true);
						}}
						size="large"
						sx={{ height: "45px", fontWeight: "bold", m: 1 }}
						variant="contained">
						گزارش عملکرد دانش آموزان
					</Button>
				</Box>
			</Box>

			{loading ? (
				<Box className="d-flex-column" mt={5}>
					درحال دریافت اطلاعات
					<CircularProgress color="secondary" sx={{ my: 2 }} />
				</Box>
			) : fetchMsg ? (
				<Box className="d-flex-column">
					<Typography mb={1}>{fetchMsg.header}</Typography>
					<Typography mb={2}>{fetchMsg.message}</Typography>
					<Button
						onClick={fetchData}
						variant="contained"
						color="secondary"
						sx={{ color: "white" }}>
						تلاش مجدد
					</Button>
				</Box>
			) : advisors.length === 0 ? (
				<Typography>
					هنوز هیچ مشاوری اضافه نکرده‌اید. با استفاده از دکمه «افزودن مشاور» اولین
					مشاور را اضافه کنید
				</Typography>
			) : (
				<TableContainer component={Paper}>
					<Table sx={{ "& .MuiTableCell-root": { padding: "10px", fontSize: "14px" } }}>
						<TableHead sx={{ backgroundColor: "#E6E1F4" }}>
							<TableRow>
								{[
									"ردیف",
									"نام",
									"نام خانوادگی",
									"شماره تلفن",
									"شهر",
									"جنسیت",
									"ظرفیت",
									"عملیات",
								].map((item, index) => (
									<TableCell key={index} sx={{ fontWeight: "bold" }}>
										{item}
									</TableCell>
								))}
							</TableRow>
						</TableHead>

						<TableBody>
							{[...advisors].reverse().map((advisor, index) => {
								return (
									<TableRow key={index}>
										<TableCell>{advisors.length - index}</TableCell>
										<TableCell>{advisor.firstName}</TableCell>
										<TableCell>{advisor.lastName}</TableCell>
										<TableCell>{advisor.mobile}</TableCell>
										<TableCell>
											{advisor.city !== undefined &&
											advisor.city !== null &&
											advisor.city !== ""
												? advisor.city
												: "نامشخص"}
										</TableCell>
										<TableCell>
											{advisor.gender !== undefined && advisor.gender !== null
												? advisor.gender === 0
													? "زن"
													: "مرد"
												: "نامشخص"}
										</TableCell>
										<TableCell>{advisor.capacity}</TableCell>
										<TableCell className="d-flex">
											<Tooltip title="ورود به حساب کاربری" arrow>
												<IconButton
													onClick={() => {
														axios({
															method: "GET",
															baseURL: Constants.baseUrl,
															url: "/school/advisor/login/" + advisor.id,
															headers: {
																authorization: "Bearer " + localStorage.getItem("schoolToken"),
															},
														})
															.then(response => {
																localStorage.setItem(
																	"appToken",
																	response.data.result.sessions.session.token
																);
																info.domain
																	? window.open(
																			`https://app.${info.domain}/app?token=${localStorage.getItem(
																				"appToken"
																			)}`
																	  )
																	: window.open("/app");
															})
															.catch(error => {
																console.log(error);
															});
													}}>
													<LoginIcon sx={{ fontSize: 27 }} color="primary" />
												</IconButton>
											</Tooltip>
											<Tooltip title="ویرایش" arrow>
												<IconButton
													onClick={() => {
														setAdvisor(advisor);
														setIdToEdit(advisor.id);
														setEditOpen(true);
														setEditMsg(null);
													}}>
													<EditIcon sx={{ fontSize: 27 }} color="secondary" />
												</IconButton>
											</Tooltip>
											<Tooltip title="حذف" arrow>
												<IconButton
													onClick={() => {
														setIdToDelete(advisor.id);
														setFullNameToDelete(advisor.firstName + " " + advisor.lastName);
														setDelOpen(true);
													}}>
													<DeleteIcon sx={{ fontSize: 27 }} color="error" />
												</IconButton>
											</Tooltip>
										</TableCell>
									</TableRow>
								);
							})}
						</TableBody>
					</Table>
				</TableContainer>
			)}

			<AddAdvisorModal
				open={open}
				setOpen={setOpen}
				addAdvisorApi={addAdvisorApi}
				addMsg={addMsg}
				setAddMsg={setAddMsg}
			/>

			<EditAdvisorModal
				open={editOpen}
				setOpen={setEditOpen}
				editAdvisorApi={editAdvisorApi}
				editMsg={editMsg}
				setEditMsg={setEditMsg}
				advisor={advisor}
				idToEdit={idToEdit}
			/>

			<DeleteAdvisorModal
				delOpen={delOpen}
				setDelOpen={setDelOpen}
				deleteApi={deleteApi}
				deleteMsg={deleteMsg}
				setDeleteMsg={setDeleteMsg}
				idToDelete={idToDelete}
				fullName={fullNameToDelete}
			/>

			<ReportModal open={reportModal} setOpen={setReportModal} />
		</>
	);
}

export default Advisors;
